import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Article, ClassificationAttribute, ColumnAttribute } from '../../model';
import { ArticleInfoAttributesFacade, PrincipalConfigurationService } from '../index';

@Injectable()
export class ArticleInfoAttributesService implements ArticleInfoAttributesFacade {
  constructor(private principalConfigurationService: PrincipalConfigurationService) {}

  getColumnAttributes(article: Article, enableSecondaryArticleRowVariant: boolean): Observable<ColumnAttribute[]> {
    return combineLatest([
      this.principalConfigurationService.isEnabled('enableDisplayGrainDirection'),
      this.principalConfigurationService.isEnabled('hideColumnAttributesForLocalSalesTextArticles'),
    ]).pipe(
      filter(
        ([_enableDisplayGrainDirection, hideColumnAttributesForLocalSalesTextArticles]) =>
          !!article &&
          !(hideColumnAttributesForLocalSalesTextArticles && article.articleNameDisplayMode === 'LOCAL_SALES_TEXT') &&
          !!article.attributes?.length
      ),
      map(([enableDisplayGrainDirection, _hideColumnAttributesForLocalSalesTextArticles]) => {
        return this.prepareColumnAttributes(article, enableDisplayGrainDirection, enableSecondaryArticleRowVariant);
      })
    );
  }

  private prepareColumnAttributes(
    article: Article,
    enableDisplayGrainDirection: boolean,
    enableSecondaryArticleRowVariant: boolean
  ): ColumnAttribute[] {
    const attributeCodes = article.attributes
      .filter((attribute) => {
        // Ignoring grain direction column attribute when enableDisplayGrainDirection config is false
        if (attribute.code === 'ZZLFR') {
          return Boolean(attribute.columnAttribute) && enableDisplayGrainDirection;
        } else {
          return Boolean(attribute.columnAttribute);
        }
      })
      .map((attribute) => attribute.code);

    return attributeCodes
      .map((attributeCode) => {
        const classificationAttribute = article.classificationAttributes[attributeCode];

        if (!classificationAttribute || classificationAttribute.value === '-') {
          return {};
        }

        return enableSecondaryArticleRowVariant
          ? this.prepareColumnAttributeForSecondaryArticleRow(classificationAttribute)
          : this.prepareColumnAttributeForDefaultArticleRow(article, attributeCodes, classificationAttribute);
      })
      .filter((value) => !!Object.keys(value).length);
  }

  private prepareColumnAttributeForDefaultArticleRow(
    article: Article,
    attributeCodes: string[],
    classificationAttribute: ClassificationAttribute
  ): ColumnAttribute {
    if (classificationAttribute.code === 'ZZLAEN' && attributeCodes.indexOf('ZZBREN') !== -1) {
      // Omitting length attribute if width exists, because both of them are concatenated into one (see condition below)
      return {};
    }

    if (classificationAttribute.code === 'ZZBREN' && attributeCodes.indexOf('ZZLAEN') !== -1) {
      // Concatenating width and length to present them as single attribute, e.g.:
      // name: Width (mm) x Length (mm)
      // value: 100x200
      const ZZLAENAttribute = article.classificationAttributes['ZZLAEN'];
      return {
        name:
          classificationAttribute.name && ZZLAENAttribute.name
            ? `${classificationAttribute.name} x ${ZZLAENAttribute.name}`
            : `${classificationAttribute.code} x ${ZZLAENAttribute.code}`,
        value: `${classificationAttribute.value}x${ZZLAENAttribute.value}`,
      };
    } else {
      return {
        name: classificationAttribute.name || classificationAttribute.code,
        value: classificationAttribute.unitString
          ? `${classificationAttribute.value} ${classificationAttribute.unitString}`
          : classificationAttribute.value,
      };
    }
  }

  // Secondary article row variant presents the attributes in different way, thus requiring different (simpler) logic than default row variant
  private prepareColumnAttributeForSecondaryArticleRow(classificationAttribute: ClassificationAttribute): ColumnAttribute {
    return {
      name: classificationAttribute.name || classificationAttribute.code,
      value: classificationAttribute.value,
    };
  }
}
